
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const useProfilePicture = () => {
  const { user } = useAuth();
  const [profilePicture, setProfilePicture] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);

  // Fetch current profile picture
  useEffect(() => {
    const fetchProfilePicture = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('profile_picture, avatar_url')
          .eq('id', user.id)
          .single();

        if (error && error.code !== 'PGRST116') {
          throw error;
        }

        // Use profile_picture first, then fall back to avatar_url
        setProfilePicture(data?.profile_picture || data?.avatar_url || null);
      } catch (error) {
        console.error('Error fetching profile picture:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfilePicture();
  }, [user]);

  // Upload profile picture
  const uploadProfilePicture = async (file: File): Promise<boolean> => {
    if (!user) return false;

    // Validate file
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return false;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB
      toast.error('File size must be less than 5MB');
      return false;
    }

    setIsUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/avatar.${fileExt}`;

      // Delete existing file if it exists
      await supabase.storage
        .from('profile-pictures')
        .remove([fileName]);

      // Upload new file
      const { error: uploadError } = await supabase.storage
        .from('profile-pictures')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('profile-pictures')
        .getPublicUrl(fileName);

      // Update profile
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ 
          profile_picture: publicUrl,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (updateError) throw updateError;

      setProfilePicture(publicUrl);
      toast.success('Profile picture updated successfully');
      return true;
    } catch (error: any) {
      console.error('Error uploading profile picture:', error);
      toast.error(error.message || 'Failed to upload profile picture');
      return false;
    } finally {
      setIsUploading(false);
    }
  };

  // Remove profile picture
  const removeProfilePicture = async (): Promise<boolean> => {
    if (!user) return false;

    setIsUploading(true);
    try {
      // Update profile to remove picture
      const { error } = await supabase
        .from('profiles')
        .update({ 
          profile_picture: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) throw error;

      setProfilePicture(null);
      toast.success('Profile picture removed');
      return true;
    } catch (error: any) {
      console.error('Error removing profile picture:', error);
      toast.error(error.message || 'Failed to remove profile picture');
      return false;
    } finally {
      setIsUploading(false);
    }
  };

  return {
    profilePicture,
    isLoading,
    isUploading,
    uploadProfilePicture,
    removeProfilePicture
  };
};
